/**
 * React Query hooks + normalizers for Cockpit notifications.
 *
 *   useNotificationTypes()        GET /v1/notifications/types
 *   useSetSubscription()          PUT /v1/notifications/subscriptions/{code}
 *   useNotificationLog(filters)   GET /v1/notifications/log   (tool it.notifications)
 *
 * The api owns the data and the senders (api#665); this file is the single
 * reconcile point for contract drift. Every read is tolerant: `retry: false`
 * plus the normalizers mean a 404 / 503 / shape mismatch surfaces as
 * `available === false` (or an empty list) rather than a crash, because the
 * backend may not be deployed when this ships (pattern: useOmni.ts).
 *
 * Every type is OFF by default. A person opts in per type, per channel, under
 * Settings > Notifications. SMS is reserved and not available yet.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut } from './api';
import { API_BASE } from './apiBase';

export const NOTIFICATIONS_API_BASE = `${API_BASE}/v1/notifications`;
export const NOTIFICATION_TYPES_QUERY_KEY = ['notification-types'] as const;

export type NotificationType = {
  code: string;
  label: string;
  description: string;
  tool_slug: string | null;
  /** Which tool the type comes from (`cockpit`, `servicedesk`). Groups the Settings page. */
  source: string;
  /** Display name of the source, shown as the group heading (`Service Desk`). */
  source_label: string;
  teams: boolean;
  email: boolean;
  sms: false;
  sms_available: false;
  /** False when the person has no @ampersandbrands.com account. */
  can_teams: boolean;
};

export type NotificationGroup = { source: string; label: string; types: NotificationType[] };

export type NotificationEvent = {
  id: number | string;
  title: string;
  summary: string;
  actor_email: string | null;
  store_no: string | null;
  deep_link: string | null;
  created_at: string | null;
};

export type NotificationSend = {
  id: number | string;
  created_at: string | null;
  sent_at: string | null;
  type_code: string;
  type_label: string;
  channel: string;
  status: string;
  skip_reason: string | null;
  error: string | null;
  recipient_email: string;
  event: NotificationEvent | null;
};

export type NotificationLogFilters = {
  type?: string;
  channel?: string;
  status?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

export type NotificationLogPage = { sends: NotificationSend[]; total_count: number };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/** Heading for a source code when the api sent no label (an older api, or a new source). */
function sourceHeading(source: string): string {
  if (source === 'cockpit') return 'Cockpit';
  if (source === 'servicedesk') return 'Service Desk';
  return source.charAt(0).toUpperCase() + source.slice(1);
}

/** One type row, or null when it lacks a usable code. */
export function normalizeNotificationType(raw: unknown): NotificationType | null {
  if (!isRecord(raw)) return null;
  const code = str(raw.code);
  if (!code) return null;
  const source = str(raw.source) ?? 'cockpit';
  return {
    code,
    label: str(raw.label) ?? code,
    description: str(raw.description) ?? '',
    tool_slug: str(raw.tool_slug),
    source,
    source_label: str(raw.source_label) ?? sourceHeading(source),
    teams: raw.teams === true,
    email: raw.email === true,
    sms: false,
    sms_available: false,
    // Default to allowed when the field is absent; the api is the real gate.
    can_teams: raw.can_teams !== false,
  };
}

/** The `types` payload as a list; null when the body is not the contract shape. */
export function normalizeNotificationTypes(raw: unknown): NotificationType[] | null {
  if (!isRecord(raw) || !Array.isArray(raw.types)) return null;
  return raw.types
    .map(normalizeNotificationType)
    .filter((t): t is NotificationType => t !== null);
}

function normalizeEvent(raw: unknown): NotificationEvent | null {
  if (!isRecord(raw)) return null;
  const id = typeof raw.id === 'number' || typeof raw.id === 'string' ? raw.id : '';
  return {
    id,
    title: str(raw.title) ?? '',
    summary: str(raw.summary) ?? '',
    actor_email: str(raw.actor_email),
    store_no: raw.store_no == null || raw.store_no === '' ? null : String(raw.store_no),
    deep_link: str(raw.deep_link),
    created_at: str(raw.created_at),
  };
}

export function normalizeNotificationSend(raw: unknown): NotificationSend | null {
  if (!isRecord(raw)) return null;
  const id = typeof raw.id === 'number' || typeof raw.id === 'string' ? raw.id : null;
  if (id === null) return null;
  return {
    id,
    created_at: str(raw.created_at),
    sent_at: str(raw.sent_at),
    type_code: str(raw.type_code) ?? '',
    type_label: str(raw.type_label) ?? str(raw.type_code) ?? '',
    channel: str(raw.channel) ?? '',
    status: str(raw.status) ?? '',
    skip_reason: str(raw.skip_reason),
    error: str(raw.error),
    recipient_email: str(raw.recipient_email) ?? '',
    event: normalizeEvent(raw.event),
  };
}

export function normalizeNotificationLog(raw: unknown): NotificationLogPage | null {
  if (!isRecord(raw) || !Array.isArray(raw.sends)) return null;
  const sends = raw.sends
    .map(normalizeNotificationSend)
    .filter((s): s is NotificationSend => s !== null);
  const total = typeof raw.total_count === 'number' ? raw.total_count : sends.length;
  return { sends, total_count: total };
}

/** Query string for the log endpoint; blank filters are dropped. */
export function logQueryString(f: NotificationLogFilters): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * Types grouped by source for the Settings page. Cockpit's own types come first,
 * then each other source (Service Desk) in the order the api listed it, and the
 * api's order is kept inside each group. One group alone needs no heading, which
 * the page decides from `groups.length`.
 */
export function groupNotificationTypes(types: NotificationType[]): NotificationGroup[] {
  const groups = new Map<string, NotificationGroup>();
  for (const t of types) {
    const g = groups.get(t.source) ?? { source: t.source, label: t.source_label, types: [] };
    g.types.push(t);
    groups.set(t.source, g);
  }
  return [...groups.values()].sort(
    (a, b) => Number(b.source === 'cockpit') - Number(a.source === 'cockpit'),
  );
}

/** Pure optimistic update: the list with one type's channel flags replaced. */
export function applyToggle(
  types: NotificationType[],
  code: string,
  next: { teams: boolean; email: boolean },
): NotificationType[] {
  return types.map((t) => (t.code === code ? { ...t, ...next } : t));
}

export type UseNotificationTypesResult = {
  types: NotificationType[];
  /** False on 404 / 503 / shape mismatch: render "Not available yet". */
  available: boolean;
  isLoading: boolean;
};

export function useNotificationTypes(): UseNotificationTypesResult {
  const query = useQuery<NotificationType[] | null, Error>({
    queryKey: NOTIFICATION_TYPES_QUERY_KEY,
    queryFn: async () =>
      normalizeNotificationTypes(await apiGet<unknown>(`${NOTIFICATIONS_API_BASE}/types`)),
    staleTime: 60_000,
    retry: false,
  });
  return {
    types: query.data ?? [],
    available: !query.isError && query.data != null,
    isLoading: query.isLoading,
  };
}

type ToggleVars = { code: string; teams: boolean; email: boolean };

/**
 * Saves one type's Teams/Email flags. Updates the cached list immediately and
 * rolls back to the previous list if the save fails.
 */
export function useSetSubscription() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, ToggleVars, { previous: NotificationType[] | null | undefined }>({
    mutationFn: ({ code, teams, email }) =>
      apiPut<unknown>(`${NOTIFICATIONS_API_BASE}/subscriptions/${encodeURIComponent(code)}`, {
        teams,
        email,
      }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: NOTIFICATION_TYPES_QUERY_KEY });
      const previous = qc.getQueryData<NotificationType[] | null>(NOTIFICATION_TYPES_QUERY_KEY);
      if (previous) {
        qc.setQueryData<NotificationType[] | null>(
          NOTIFICATION_TYPES_QUERY_KEY,
          applyToggle(previous, vars.code, { teams: vars.teams, email: vars.email }),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(NOTIFICATION_TYPES_QUERY_KEY, ctx.previous);
    },
  });
}

export type UseNotificationLogResult = {
  sends: NotificationSend[];
  totalCount: number;
  available: boolean;
  isLoading: boolean;
};

export function useNotificationLog(filters: NotificationLogFilters): UseNotificationLogResult {
  const query = useQuery<NotificationLogPage | null, Error>({
    queryKey: ['notification-log', filters],
    queryFn: async () =>
      normalizeNotificationLog(
        await apiGet<unknown>(`${NOTIFICATIONS_API_BASE}/log${logQueryString(filters)}`),
      ),
    staleTime: 30_000,
    retry: false,
  });
  return {
    sends: query.data?.sends ?? [],
    totalCount: query.data?.total_count ?? 0,
    available: !query.isError && query.data != null,
    isLoading: query.isLoading,
  };
}

/** Status pill tones for the log: sent green, failed red, skipped neutral, queued amber. */
export function sendStatusTone(status: string): string {
  switch (status) {
    case 'sent':
      return 'bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-200';
    case 'failed':
      return 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200';
    case 'queued':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200';
    case 'skipped':
    default:
      return 'bg-amp-line/60 text-amp-ink dark:bg-amp-dark-line/60 dark:text-amp-dark-ink';
  }
}

/** Distinct type options (code + label) found in the loaded rows, sorted by label. */
export function typeOptionsFromRows(
  rows: Pick<NotificationSend, 'type_code' | 'type_label'>[],
): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const r of rows) if (r.type_code && !seen.has(r.type_code)) seen.set(r.type_code, r.type_label);
  return [...seen]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
